import {
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  Contract,
  Invoice,
  InvoiceWeekReport,
  Payment,
  WeekReport,
  WeekReportLine
} from '@/api/generated/models';
import type { CardTab, InfoField } from '@/components/common';
import { useListContracts } from '@/api/generated/contracts/contracts';
import {
  getGetInvoiceQueryKey,
  getListInvoicesQueryKey,
  getListInvoiceWeekReportsQueryKey,
  useAcceptInvoice,
  useApproveInvoice,
  useCancelInvoice,
  useCloseInvoice,
  useDeleteInvoice,
  useGetInvoice,
  useListInvoiceWeekReports,
  useMarkOverdue,
  useRejectInvoice,
  useRemoveWeekReport,
  useSendInvoice
} from '@/api/generated/invoices/invoices';
import { InvoiceStatus } from '@/api/generated/models/invoiceStatus';
import { useListPayments } from '@/api/generated/payments/payments';
import { getWeekReport, listWeekReportLines } from '@/api/generated/week-reports/week-reports';
import { IconFigmaXCircle } from '@/assets/icons';
import {
  AddActionButton,
  BackButton,
  CardTabs,
  ConfirmActionModal,
  fitColumnWidth,
  InfoCard,
  ListPagination,
  MobileActionBar,
  MobileCard,
  MobileCardList,
  MobileCodeBadge,
  MobileField,
  MutationButton,
  StatusBanner,
  StatusPill,
  StatusTracker,
  tableRowStyle,
  Toast
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { AddWeekReportToInvoiceModal } from '@/features/invoices/components/AddWeekReportToInvoiceModal';
import { RecordPaymentModal } from '@/features/invoices/components/RecordPaymentModal';
import { RejectInvoiceModal } from '@/features/invoices/components/RejectInvoiceModal';
import {
  buildInvoiceTrackerSteps,
  formatPaymentMethod,
  statusConfig
} from '@/features/invoices/invoiceDisplay';
import { formatWeekLabel, formatWeekPeriod } from '@/features/week-reports/weekReportDisplay';
import { formatAmount, formatDate, otherPartyName } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const cellStyle = { color: 'var(--color-text-secondary)' };

const TERMINAL_STATUSES: string[] = [
  InvoiceStatus.CANCELLED,
  InvoiceStatus.CLOSED,
  InvoiceStatus.PAID,
  InvoiceStatus.CREDITED,
  InvoiceStatus.REJECTED
];
const PAYABLE_STATUSES: string[] = [
  InvoiceStatus.SENT,
  InvoiceStatus.ACCEPTED,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PAID
];

const itemColumns: [string, number, 'left' | 'right'][] = [
  ['invoiceDetail.colCode', 100, 'left'],
  ['invoiceDetail.colDescription', 340, 'left'],
  ['invoiceDetail.colQty', 100, 'right'],
  ['invoiceDetail.colUnit', 100, 'left'],
  ['invoiceDetail.colPrice', 120, 'right'],
  ['invoiceDetail.colAmount', 140, 'right']
];

export const InvoiceDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('weekReports');
  const [addWeekReportOpen, setAddWeekReportOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const handlePreview = async () => {
    if (!id) return;
    setPreviewError(false);
    setIsPreviewLoading(true);
    try {
      await openBinaryInNewTab(`/api/v1/invoices/${id}/pdf`);
    } catch {
      setPreviewError(true);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const { data: invoiceData, isLoading } = useGetInvoice(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const invoice = invoiceData as Invoice | undefined;

  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contract = contracts.find((c) => c.id === invoice?.contractId);

  const isPartnerView = !!user && !!contract && user.organisationId === contract.partnerOrgId;
  const isEditable = invoice?.status === InvoiceStatus.DRAFT;

  const { data: linkedData } = useListInvoiceWeekReports(id ?? '');
  const linkedWeekReports: InvoiceWeekReport[] = linkedData ?? [];
  const weekReportIds = useMemo(
    () => linkedWeekReports.map((l) => l.weekReportId).filter((v): v is string => !!v),
    [linkedWeekReports]
  );

  const weekReportQueries = useQueries({
    queries: weekReportIds.map((wrId) => ({
      queryKey: ['week-report', wrId],
      queryFn: () => getWeekReport(wrId),
      enabled: !!wrId
    }))
  });
  const lineQueries = useQueries({
    queries: weekReportIds.map((wrId) => ({
      queryKey: ['week-report-lines', wrId],
      queryFn: () => listWeekReportLines(wrId),
      enabled: !!wrId
    }))
  });

  const weekReportMap = new Map(
    weekReportIds.map((wrId, i) => [wrId, weekReportQueries[i]?.data as WeekReport | undefined])
  );
  const lineItems: WeekReportLine[] = lineQueries.flatMap(
    (q) => (q.data as WeekReportLine[] | undefined) ?? []
  );

  const { data: paymentsData } = useListPayments(id ?? '', { query: { enabled: !!id } });
  const payments: Payment[] = paymentsData ?? [];

  const {
    page: weekReportsPage,
    setPage: setWeekReportsPage,
    pageSize: weekReportsPageSize,
    setPageSize: setWeekReportsPageSize,
    totalPages: weekReportsTotalPages,
    pageItems: pageLinkedWeekReports
  } = useLocalPagination(linkedWeekReports);
  const reportColWidth = fitColumnWidth(
    pageLinkedWeekReports.map((link) => {
      const report = link.weekReportId ? weekReportMap.get(link.weekReportId) : undefined;
      return formatWeekLabel(report?.weekNumber, report?.reportYear);
    }),
    '600 13.5px',
    100,
    240
  );

  const {
    page: itemsPage,
    setPage: setItemsPage,
    pageSize: itemsPageSize,
    setPageSize: setItemsPageSize,
    totalPages: itemsTotalPages,
    pageItems: pageLineItems
  } = useLocalPagination(lineItems);

  const {
    page: paymentsPage,
    setPage: setPaymentsPage,
    pageSize: paymentsPageSize,
    setPageSize: setPaymentsPageSize,
    totalPages: paymentsTotalPages,
    pageItems: pagePayments
  } = useLocalPagination(payments);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
  };

  const approveMutation = useApproveInvoice({ mutation: { onSuccess: invalidate } });
  const sendMutation = useSendInvoice({ mutation: { onSuccess: invalidate } });
  const acceptMutation = useAcceptInvoice({ mutation: { onSuccess: invalidate } });
  const rejectMutation = useRejectInvoice({
    mutation: {
      onSuccess: () => {
        invalidate();
        setRejectModalOpen(false);
      }
    }
  });
  const removeWeekReportMutation = useRemoveWeekReport({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries({ queryKey: getListInvoiceWeekReportsQueryKey(id) });
      }
    }
  });
  const markOverdueMutation = useMarkOverdue({ mutation: { onSuccess: invalidate } });
  const closeMutation = useCloseInvoice({ mutation: { onSuccess: invalidate } });
  const cancelMutation = useCancelInvoice({
    mutation: {
      onSuccess: () => {
        invalidate();
        setCancelModalOpen(false);
      }
    }
  });
  const deleteMutation = useDeleteInvoice({
    mutation: {
      onSuccess: () => {
        setDeleteModalOpen(false);
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        // Search ključ nosi parametre (stranica/filteri) — invalidira se po prefiksu putanje.
        queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].startsWith('/api/v1/invoices/search')
        });
        navigate('/invoices', { state: { savedMessage: t('invoiceDetail.deleteSuccess') } });
      }
    }
  });

  if (isLoading || !invoice) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  const config = statusConfig[invoice.status ?? InvoiceStatus.DRAFT];
  const trackerSteps = buildInvoiceTrackerSteps(
    invoice.status ?? InvoiceStatus.DRAFT,
    isPartnerView
  );
  // Broj fakture ne postoji dok nije validirana (DRAFT) — prikazuje se "Nacrt" umjesto sirovog referenceId.
  const invoiceLabel = invoice.invoiceNumber ?? t('invoiceDetail.draftLabel');
  const taxRatePercent = invoice.taxRate
    ? invoice.taxRate > 0 && invoice.taxRate < 1
      ? invoice.taxRate * 100
      : invoice.taxRate
    : 0;
  // PDV se povlači iz ugovora (InvoiceService) — ako ugovor nema PDV, taxAmount je 0 i redak
  // Međuzbroj/PDV se uopće ne prikazuje (isto kao showVat na Ponudi/Ugovoru), samo Ukupno.
  const hasVat = !!invoice.taxAmount && invoice.taxAmount > 0;
  const isOwner = !!user && user.organisationId === invoice.ownerOrgId;
  // Partner ostaje partner na obje kopije (svoja i klijentova, vidi InvoiceService.send())
  // — za razliku od isOwner (uvijek true za bilo koju fakturu koju uopće mogu vidjeti, jer
  // RLS filtrira po vlastitoj kopiji), ovo razlikuje partnera od klijenta na SENT+ statusima
  // gdje obje kopije istovremeno postoje.
  const isPartnerOrg = !!user && user.organisationId === invoice.partnerOrgId;
  const hasWeekReports = linkedWeekReports.length > 0;
  const canApprove = invoice.status === InvoiceStatus.DRAFT && isOwner && hasWeekReports;
  const canSend = invoice.status === InvoiceStatus.APPROVED && isOwner;

  const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : undefined;
  const dueDays = dueDate
    ? Math.round((dueDate.getTime() - new Date().setHours(0, 0, 0, 0)) / (24 * 60 * 60 * 1000))
    : undefined;

  const canRecordPayment = isPartnerOrg && PAYABLE_STATUSES.includes(invoice.status ?? '');
  const canMarkOverdue =
    isPartnerOrg &&
    (invoice.status === InvoiceStatus.SENT ||
      invoice.status === InvoiceStatus.ACCEPTED ||
      invoice.status === InvoiceStatus.PARTIALLY_PAID) &&
    dueDays !== undefined &&
    dueDays < 0;
  const canClose = isPartnerOrg && invoice.status === InvoiceStatus.PAID;
  // Nacrt se briše (nema broja ni klijentove kopije, vidi InvoiceService.delete), sve nakon
  // toga samo otkazuje — svaki status ima točno jednu "ukloni" akciju (isto kao ugovori).
  const canDelete = isPartnerOrg && invoice.status === InvoiceStatus.DRAFT;
  const canCancel =
    isPartnerOrg &&
    invoice.status !== InvoiceStatus.DRAFT &&
    !TERMINAL_STATUSES.includes(invoice.status ?? '');
  // accept()/reject() — samo klijent, samo na SENT (isti obrazac kao OfferDetailPage.canAct).
  const canAcceptReject = !isPartnerOrg && invoice.status === InvoiceStatus.SENT;
  const isAccepted = invoice.status === InvoiceStatus.ACCEPTED;
  const isRejected = invoice.status === InvoiceStatus.REJECTED;
  const canShowPaymentsTab =
    invoice.status !== InvoiceStatus.DRAFT && invoice.status !== InvoiceStatus.APPROVED;
  const dueLabel =
    dueDays === undefined
      ? undefined
      : dueDays > 0
        ? t('invoiceDetail.dueInDays', { count: dueDays })
        : dueDays === 0
          ? t('invoiceDetail.dueToday')
          : t('invoiceDetail.overdueSince', { count: Math.abs(dueDays) });

  const infoFields: InfoField[] = [
    {
      label: t('invoiceDetail.kpiContract'),
      value: contract?.contractNumber ?? '—',
      ...(invoice.contractId ? { onClick: () => navigate(`/contracts/${invoice.contractId}`) } : {})
    },
    {
      label: isPartnerView ? t('invoiceDetail.kpiClient') : t('invoiceDetail.partner'),
      value: contract ? otherPartyName(contract, user?.orgName) : '—'
    },
    { label: t('invoiceDetail.issueDate'), value: formatDate(invoice.issueDate) },
    {
      label: t('invoiceDetail.dueDate'),
      value: dueLabel ? `${formatDate(invoice.dueDate)} · ${dueLabel}` : formatDate(invoice.dueDate)
    },
    { label: t('invoiceDetail.kpiTotal'), value: formatAmount(invoice.totalAmount) }
  ];

  const weekReportsTab: CardTab = {
    key: 'weekReports',
    label: t('invoiceDetail.weekReportsTab'),
    caption: t('invoiceDetail.weekReportsCaption'),
    headerRight: isEditable && (
      <AddActionButton onClick={() => setAddWeekReportOpen(true)}>
        {t('invoiceDetail.addWeekReportButton')}
      </AddActionButton>
    ),
    content: (
      <div>
        <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              <Text
                fz={10}
                fw={600}
                w={reportColWidth}
                style={{ color: 'var(--color-text-muted)' }}
              >
                {t('invoiceDetail.colReport')}
              </Text>
              <Text fz={10} fw={600} w={180} style={{ color: 'var(--color-text-muted)' }}>
                {t('invoiceDetail.colPeriod')}
              </Text>
              <Text
                fz={10}
                fw={600}
                w={110}
                ta="right"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {t('invoiceDetail.colAmount')}
              </Text>
              <Text
                fz={10}
                fw={600}
                w={100}
                ta="right"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {t('invoiceDetail.colAction')}
              </Text>
              {isEditable && <Box w={40} />}
            </div>

            {pageLinkedWeekReports.map((link, index) => {
              const report = link.weekReportId ? weekReportMap.get(link.weekReportId) : undefined;
              return (
                <div
                  key={link.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text
                    fz={13.5}
                    fw={600}
                    w={reportColWidth}
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {formatWeekLabel(report?.weekNumber, report?.reportYear)}
                  </Text>
                  <Text fz={13} w={180} style={cellStyle}>
                    {formatWeekPeriod(report?.weekNumber, report?.reportYear)}
                  </Text>
                  <Text
                    fz={13.5}
                    fw={600}
                    w={110}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {formatAmount(link.wrAmount)}
                  </Text>
                  <Text
                    fz={12}
                    fw={600}
                    w={100}
                    ta="right"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      link.weekReportId && navigate(`/weekly-reports/${link.weekReportId}`)
                    }
                    onKeyDown={(event) => {
                      if ((event.key === 'Enter' || event.key === ' ') && link.weekReportId) {
                        navigate(`/weekly-reports/${link.weekReportId}`);
                      }
                    }}
                    style={{ color: 'var(--color-accent-primary-active)', cursor: 'pointer' }}
                  >
                    {t('invoices.details')}
                  </Text>
                  {isEditable && (
                    <Group w={40} justify="flex-end">
                      <Box
                        role="button"
                        tabIndex={0}
                        title={t('invoiceDetail.removeWeekReportAction')}
                        onClick={() => {
                          if (id && link.weekReportId) {
                            removeWeekReportMutation.mutate({
                              id,
                              weekReportId: link.weekReportId
                            });
                          }
                        }}
                        style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
                      >
                        <IconFigmaXCircle size={16} />
                      </Box>
                    </Group>
                  )}
                </div>
              );
            })}
          </div>
        </Box>

        <MobileCardList>
          {pageLinkedWeekReports.map((link) => {
            const report = link.weekReportId ? weekReportMap.get(link.weekReportId) : undefined;
            const openReport = () =>
              link.weekReportId && navigate(`/weekly-reports/${link.weekReportId}`);
            return (
              <MobileCard key={link.id}>
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
                  <MobileField label={t('invoiceDetail.colReport')}>
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {formatWeekLabel(report?.weekNumber, report?.reportYear)}
                    </Text>
                  </MobileField>
                  {isEditable && (
                    <Box
                      role="button"
                      tabIndex={0}
                      title={t('invoiceDetail.removeWeekReportAction')}
                      onClick={() => {
                        if (id && link.weekReportId) {
                          removeWeekReportMutation.mutate({ id, weekReportId: link.weekReportId });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        color: 'var(--color-status-danger)',
                        flexShrink: 0
                      }}
                    >
                      <IconFigmaXCircle size={18} />
                    </Box>
                  )}
                </Group>
                <MobileField label={t('invoiceDetail.colPeriod')}>
                  <Text fz={13} style={cellStyle}>
                    {formatWeekPeriod(report?.weekNumber, report?.reportYear)}
                  </Text>
                </MobileField>
                <Group justify="space-between" align="flex-end" wrap="nowrap" gap={8}>
                  <MobileField label={t('invoiceDetail.colAmount')}>
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {formatAmount(link.wrAmount)}
                    </Text>
                  </MobileField>
                  <Text
                    fz={13}
                    fw={600}
                    role="button"
                    tabIndex={0}
                    onClick={openReport}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') openReport();
                    }}
                    style={{ color: 'var(--color-accent-primary-active)', cursor: 'pointer' }}
                  >
                    {t('invoices.details')}
                  </Text>
                </Group>
              </MobileCard>
            );
          })}
        </MobileCardList>

        {linkedWeekReports.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.noWeekReports')}
            </Text>
          </Center>
        )}

        {linkedWeekReports.length > 0 && (
          <ListPagination
            label={t('invoiceDetail.weekReportsShowing', {
              shown: pageLinkedWeekReports.length,
              total: linkedWeekReports.length
            })}
            page={weekReportsPage}
            totalPages={weekReportsTotalPages}
            onPageChange={setWeekReportsPage}
            pageSize={weekReportsPageSize}
            onPageSizeChange={setWeekReportsPageSize}
          />
        )}
      </div>
    )
  };

  const itemsTab: CardTab = {
    key: 'items',
    label: t('invoiceDetail.itemsTab'),
    caption: t('invoiceDetail.itemsCaption'),
    content: (
      <div>
        <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 900 }}>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              {itemColumns.map(([key, width, align]) => (
                <Text
                  key={key}
                  fz={10}
                  fw={600}
                  ta={align}
                  style={{ color: 'var(--color-text-muted)', width }}
                >
                  {t(key)}
                </Text>
              ))}
            </div>

            {pageLineItems.map((line, index) => (
              <div
                key={line.id}
                style={{
                  ...tableRowStyle,
                  padding: '14px 20px',
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Text fz={12} style={{ color: 'var(--color-text-muted)', width: 100 }}>
                  {line.itemCode ?? '—'}
                </Text>
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)', width: 340 }}>
                  {line.description}
                </Text>
                <Text fz={13} ta="right" style={{ color: 'var(--color-text-primary)', width: 100 }}>
                  {line.realizedQty ?? '—'}
                </Text>
                <Text fz={13} style={{ ...cellStyle, width: 100 }}>
                  {line.unit ?? '—'}
                </Text>
                <Text fz={13} ta="right" style={{ ...cellStyle, width: 120 }}>
                  {formatAmount(line.snapshotPrice)}
                </Text>
                <Text
                  fz={13.5}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)', width: 140 }}
                >
                  {formatAmount(line.lineAmount)}
                </Text>
              </div>
            ))}
          </div>
        </Box>

        <MobileCardList>
          {pageLineItems.map((line) => (
            <MobileCard key={line.id}>
              <MobileCodeBadge>
                {t('common.codeBadge', { code: line.itemCode ?? '—' })}
              </MobileCodeBadge>
              <MobileField label={t('invoiceDetail.colDescription')}>
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {line.description}
                </Text>
              </MobileField>
              <SimpleGrid cols={3} spacing={8}>
                <MobileField label={t('invoiceDetail.colQty')}>
                  <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                    {line.realizedQty ?? '—'} {line.unit ?? ''}
                  </Text>
                </MobileField>
                <MobileField label={t('invoiceDetail.colPrice')}>
                  <Text fz={13} style={cellStyle}>
                    {formatAmount(line.snapshotPrice)}
                  </Text>
                </MobileField>
                <MobileField label={t('invoiceDetail.colAmount')} align="right">
                  <Text
                    fz={13.5}
                    fw={600}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {formatAmount(line.lineAmount)}
                  </Text>
                </MobileField>
              </SimpleGrid>
            </MobileCard>
          ))}
        </MobileCardList>

        {lineItems.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.noLines')}
            </Text>
          </Center>
        )}

        {lineItems.length > 0 && (
          <ListPagination
            label={t('invoiceDetail.itemsShowing', {
              shown: pageLineItems.length,
              total: lineItems.length
            })}
            page={itemsPage}
            totalPages={itemsTotalPages}
            onPageChange={setItemsPage}
            pageSize={itemsPageSize}
            onPageSizeChange={setItemsPageSize}
          />
        )}
      </div>
    )
  };

  const paymentsTab: CardTab = {
    key: 'payments',
    label: t('invoiceDetail.paymentsTitle'),
    caption: t('invoiceDetail.paymentsCaption'),
    headerRight: canRecordPayment && (
      <AddActionButton onClick={() => setRecordPaymentOpen(true)}>
        {t('invoiceDetail.recordPaymentButton')}
      </AddActionButton>
    ),
    content: (
      <div>
        {(invoice.paidAmount ?? 0) > 0 && (
          <Group
            justify="space-between"
            px={20}
            py={14}
            style={{
              borderBottom: '1px solid var(--color-border-soft)',
              backgroundColor: 'var(--color-bg-canvas)'
            }}
          >
            <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
              {t('invoiceDetail.paidSummary', {
                paid: formatAmount(invoice.paidAmount),
                remaining: formatAmount((invoice.totalAmount ?? 0) - (invoice.paidAmount ?? 0))
              })}
            </Text>
          </Group>
        )}

        {payments.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.noPayments')}
            </Text>
          </Center>
        )}

        {payments.length > 0 && (
          <div>
            {(invoice.paidAmount ?? 0) > 0 && <Box hiddenFrom="sm" h={12} />}
            <MobileCardList>
              {pagePayments.map((payment) => (
                <MobileCard key={payment.id}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
                    <MobileField label={t('invoiceDetail.colPaymentDate')}>
                      <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                        {formatDate(payment.paymentDate)}
                      </Text>
                    </MobileField>
                    <MobileField label={t('invoiceDetail.colPaymentMethod')} align="right">
                      <Text fz={13} ta="right" style={cellStyle}>
                        {formatPaymentMethod(payment.paymentMethod) ?? '—'}
                      </Text>
                    </MobileField>
                  </Group>
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
                    <MobileField label={t('invoiceDetail.colPaymentNote')}>
                      <Text fz={13} style={cellStyle}>
                        {[payment.reference, payment.note].filter(Boolean).join(' · ') || '—'}
                      </Text>
                    </MobileField>
                    <MobileField label={t('invoiceDetail.colAmount')} align="right">
                      <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                        {formatAmount(payment.amount)}
                      </Text>
                    </MobileField>
                  </Group>
                </MobileCard>
              ))}
            </MobileCardList>
            <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 700 }}>
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text fz={10} fw={600} w={110} style={{ color: 'var(--color-text-muted)' }}>
                    {t('invoiceDetail.colPaymentDate')}
                  </Text>
                  <Text fz={10} fw={600} w={160} style={{ color: 'var(--color-text-muted)' }}>
                    {t('invoiceDetail.colPaymentMethod')}
                  </Text>
                  <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
                    {t('invoiceDetail.colPaymentNote')}
                  </Text>
                  <Text
                    fz={10}
                    fw={600}
                    w={120}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    {t('invoiceDetail.colAmount')}
                  </Text>
                </div>

                {pagePayments.map((payment, index) => (
                  <div
                    key={payment.id}
                    style={{
                      ...tableRowStyle,
                      padding: '14px 20px',
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Text fz={13} w={110} style={{ color: 'var(--color-text-primary)' }}>
                      {formatDate(payment.paymentDate)}
                    </Text>
                    <Text fz={13} w={160} style={cellStyle}>
                      {formatPaymentMethod(payment.paymentMethod) ?? '—'}
                    </Text>
                    <Text fz={13} style={{ ...cellStyle, flex: 1 }} truncate>
                      {[payment.reference, payment.note].filter(Boolean).join(' · ') || '—'}
                    </Text>
                    <Text
                      fz={13.5}
                      fw={600}
                      w={120}
                      ta="right"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {formatAmount(payment.amount)}
                    </Text>
                  </div>
                ))}
              </div>
            </Box>

            <ListPagination
              label={t('invoiceDetail.paymentsShowing', {
                shown: pagePayments.length,
                total: payments.length
              })}
              page={paymentsPage}
              totalPages={paymentsTotalPages}
              onPageChange={setPaymentsPage}
              pageSize={paymentsPageSize}
              onPageSizeChange={setPaymentsPageSize}
            />
          </div>
        )}
      </div>
    )
  };

  const totalsBlock = (
    <Stack
      gap={12}
      p={20}
      w={340}
      style={{
        maxWidth: '100%',
        borderRadius: 12,
        border: '1px solid var(--color-border-soft)',
        backgroundColor: 'var(--color-bg-surface)'
      }}
    >
      {hasVat && (
        <>
          <Group justify="space-between">
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.subtotal')}
            </Text>
            <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(invoice.subtotal)}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('invoiceDetail.tax', { rate: taxRatePercent })}
            </Text>
            <Text fz={14} fw={500} style={{ color: 'var(--color-text-primary)' }}>
              {formatAmount(invoice.taxAmount)}
            </Text>
          </Group>
        </>
      )}
      <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />
      <Group justify="space-between">
        <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('invoiceDetail.total')}
        </Text>
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {formatAmount(invoice.totalAmount)}
        </Text>
      </Group>
    </Stack>
  );

  const tabs: CardTab[] = [
    ...(isPartnerView ? [weekReportsTab] : []),
    itemsTab,
    ...(canShowPaymentsTab ? [paymentsTab] : [])
  ];
  // Isto pravilo padanja na prvi tab kao CardTabs interno (klijent nema
  // weekReportsTab, pa 'weekReports' nikad ne postoji u tabs — bez ovoga bi
  // totalsBlock ostao skriven jer activeTab state nikad ne prati taj fallback.
  const effectiveTabKey = tabs.find((tab) => tab.key === activeTab)?.key ?? tabs[0]?.key;

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/invoices" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {previewError && <Toast tone="danger">{t('invoiceDetail.previewError')}</Toast>}

        <Group justify="space-between" align="flex-start" wrap="wrap" visibleFrom="sm">
          <Stack gap={4}>
            <Group gap={12} wrap="wrap">
              <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
                {t('invoiceDetail.title', { number: invoiceLabel })}
              </Title>
              <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
            </Group>
            <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
              {t('invoiceDetail.subtitle')}
            </Text>
          </Stack>
          <Group gap={12}>
            {canApprove && (
              <MutationButton
                isPending={approveMutation.isPending}
                isPaused={approveMutation.isPaused}
                onClick={() => id && approveMutation.mutate({ id })}
              >
                {t('invoiceDetail.approve')}
              </MutationButton>
            )}
            {canSend && (
              <MutationButton
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                onClick={() => id && sendMutation.mutate({ id })}
              >
                {t('invoiceDetail.send')}
              </MutationButton>
            )}
            {canMarkOverdue && (
              <MutationButton
                variant="outline"
                isPending={markOverdueMutation.isPending}
                isPaused={markOverdueMutation.isPaused}
                onClick={() => id && markOverdueMutation.mutate({ id })}
              >
                {t('invoiceDetail.markOverdue')}
              </MutationButton>
            )}
            {canClose && (
              <MutationButton
                isPending={closeMutation.isPending}
                isPaused={closeMutation.isPaused}
                onClick={() => id && closeMutation.mutate({ id })}
              >
                {t('invoiceDetail.close')}
              </MutationButton>
            )}
            {canCancel && (
              <Button color="red" variant="outline" onClick={() => setCancelModalOpen(true)}>
                {t('invoiceDetail.cancel')}
              </Button>
            )}
            {canDelete && (
              <Button color="red" variant="outline" onClick={() => setDeleteModalOpen(true)}>
                {t('invoiceDetail.delete')}
              </Button>
            )}
            {canAcceptReject && (
              <>
                <Button color="red" variant="outline" onClick={() => setRejectModalOpen(true)}>
                  {t('invoiceDetail.reject')}
                </Button>
                <MutationButton
                  isPending={acceptMutation.isPending}
                  isPaused={acceptMutation.isPaused}
                  onClick={() => id && acceptMutation.mutate({ id })}
                >
                  {t('invoiceDetail.accept')}
                </MutationButton>
              </>
            )}
            {hasWeekReports && (
              <MutationButton variant="filled" isPending={isPreviewLoading} onClick={handlePreview}>
                {t('invoiceDetail.previewButton')}
              </MutationButton>
            )}
          </Group>
        </Group>

        <Stack gap={8} hiddenFrom="sm">
          <Group justify="space-between" wrap="nowrap" align="center">
            <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
              {t('invoiceDetail.title', { number: invoiceLabel })}
            </Title>
            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
          </Group>
          <Text fz={13.5} style={{ color: 'var(--color-text-secondary)' }}>
            {t('invoiceDetail.subtitle')}
          </Text>
          <MobileActionBar>
            {canApprove && (
              <MutationButton
                fullWidth
                isPending={approveMutation.isPending}
                isPaused={approveMutation.isPaused}
                onClick={() => id && approveMutation.mutate({ id })}
              >
                {t('invoiceDetail.approve')}
              </MutationButton>
            )}
            {canSend && (
              <MutationButton
                fullWidth
                isPending={sendMutation.isPending}
                isPaused={sendMutation.isPaused}
                onClick={() => id && sendMutation.mutate({ id })}
              >
                {t('invoiceDetail.send')}
              </MutationButton>
            )}
            {canMarkOverdue && (
              <MutationButton
                fullWidth
                variant="outline"
                isPending={markOverdueMutation.isPending}
                isPaused={markOverdueMutation.isPaused}
                onClick={() => id && markOverdueMutation.mutate({ id })}
              >
                {t('invoiceDetail.markOverdue')}
              </MutationButton>
            )}
            {canClose && (
              <MutationButton
                fullWidth
                isPending={closeMutation.isPending}
                isPaused={closeMutation.isPaused}
                onClick={() => id && closeMutation.mutate({ id })}
              >
                {t('invoiceDetail.close')}
              </MutationButton>
            )}
            {canCancel && (
              <Button
                color="red"
                fullWidth
                variant="outline"
                onClick={() => setCancelModalOpen(true)}
              >
                {t('invoiceDetail.cancel')}
              </Button>
            )}
            {canDelete && (
              <Button
                color="red"
                fullWidth
                variant="outline"
                onClick={() => setDeleteModalOpen(true)}
              >
                {t('invoiceDetail.delete')}
              </Button>
            )}
            {canAcceptReject && (
              <MutationButton
                fullWidth
                isPending={acceptMutation.isPending}
                isPaused={acceptMutation.isPaused}
                onClick={() => id && acceptMutation.mutate({ id })}
              >
                {t('invoiceDetail.accept')}
              </MutationButton>
            )}
            {canAcceptReject && (
              <Button
                color="red"
                fullWidth
                variant="outline"
                onClick={() => setRejectModalOpen(true)}
              >
                {t('invoiceDetail.reject')}
              </Button>
            )}
            {hasWeekReports && (
              <MutationButton
                variant="filled"
                isPending={isPreviewLoading}
                onClick={handlePreview}
                fullWidth
              >
                {t('invoiceDetail.previewButton')}
              </MutationButton>
            )}
          </MobileActionBar>
        </Stack>

        {invoice.status === InvoiceStatus.SENT && (
          <Box style={{ width: '100%' }}>
            <StatusBanner tone="success" message={t('invoiceDetail.sentBanner')} />
          </Box>
        )}
        {isAccepted && (
          <Box style={{ width: '100%' }}>
            <StatusBanner tone="success" message={t('invoiceDetail.acceptedBanner')} />
          </Box>
        )}
        {isRejected && (
          <Box style={{ width: '100%' }}>
            <StatusBanner
              tone="danger"
              message={t('invoiceDetail.rejectedBanner', {
                reason: invoice.rejectionReason ?? '—'
              })}
            />
          </Box>
        )}

        <StatusTracker steps={trackerSteps} />

        <InfoCard
          fields={infoFields}
          columns={infoFields.length}
          borderColor="var(--color-border-default)"
        />

        <Box style={{ overflowX: 'auto', width: '100%' }}>
          <CardTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
        </Box>

        {effectiveTabKey === 'items' && (
          <Group justify="flex-end" style={{ width: '100%' }}>
            {totalsBlock}
          </Group>
        )}
      </Stack>

      {invoice.contractId && (
        <AddWeekReportToInvoiceModal
          opened={addWeekReportOpen}
          onClose={() => setAddWeekReportOpen(false)}
          invoiceId={id ?? ''}
          contractId={invoice.contractId}
          excludeWeekReportIds={weekReportIds}
        />
      )}

      <RecordPaymentModal
        opened={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        invoiceId={id ?? ''}
        remainingAmount={(invoice.totalAmount ?? 0) - (invoice.paidAmount ?? 0)}
        onRecorded={setSavedMessage}
      />

      <ConfirmActionModal
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={() => id && cancelMutation.mutate({ id })}
        title={t('invoiceDetail.cancelModalTitle')}
        message={t('invoiceDetail.cancelModalMessage', {
          number: invoiceLabel
        })}
        confirmLabel={t('invoiceDetail.cancelModalConfirm')}
        danger
        loading={cancelMutation.isPending}
        isPaused={cancelMutation.isPaused}
      />

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => id && deleteMutation.mutate({ id })}
        title={t('invoiceDetail.deleteModalTitle')}
        message={t('invoiceDetail.deleteModalMessage')}
        confirmLabel={t('invoiceDetail.deleteModalConfirm')}
        danger
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
      />

      <RejectInvoiceModal
        opened={rejectModalOpen}
        invoiceLabel={invoiceLabel}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => id && rejectMutation.mutate({ id, data: { reason } })}
        loading={rejectMutation.isPending}
        isPaused={rejectMutation.isPaused}
      />
    </Container>
  );
};
